import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  ChevronRight,
  ChevronDown,
  Folder,
  FolderOpen,
  FileText,
  File,
  Calendar,
} from "lucide-react";

// ─── Types ──────────────────────────────────────────────────────────────────────

export interface DocumentData {
  _id: string;
  title: string;
  type: string;
  createdAt: string;
  currentVersionId?: {
    _id: string;
    file?: { filename: string; mimeType: string; sizeBytes: number };
    storage?: { key: string };
  } | null;
}

export interface MatterGroup {
  matter: {
    _id: string;
    title: string;
    matterNumber?: string;
    status?: string;
  };
  documents: DocumentData[];
}

interface DocumentTreeProps {
  matters: MatterGroup[];
  unassigned: DocumentData[];
  onDocumentClick?: (docId: string) => void;
  loading?: boolean;
}

// ─── Helpers ────────────────────────────────────────────────────────────────────

function formatBytes(bytes: number): string {
  if (!bytes) return "--";
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + " " + sizes[i];
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function getFileIcon(mimeType?: string) {
  if (!mimeType) return File;
  if (
    mimeType.includes("pdf") ||
    mimeType.includes("word") ||
    mimeType.includes("document") ||
    mimeType.includes("text")
  ) {
    return FileText;
  }
  return File;
}

// ─── Skeleton ───────────────────────────────────────────────────────────────────

const SkeletonRow: React.FC = () => (
  <div className="flex items-center gap-3 px-4 py-3 animate-pulse">
    <div className="h-4 w-4 bg-slate-200 rounded" />
    <div className="flex-1">
      <div className="h-4 w-48 bg-slate-200 rounded" />
    </div>
    <div className="h-3 w-20 bg-slate-200 rounded" />
    <div className="h-3 w-16 bg-slate-200 rounded" />
  </div>
);

const SkeletonSection: React.FC = () => (
  <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden mb-3">
    <div className="flex items-center gap-3 px-4 py-3 animate-pulse">
      <div className="h-4 w-4 bg-slate-200 rounded" />
      <div className="h-4 w-4 bg-slate-200 rounded" />
      <div className="h-4 w-40 bg-slate-200 rounded" />
      <div className="h-5 w-12 bg-slate-200 rounded-full ml-auto" />
    </div>
    <SkeletonRow />
    <SkeletonRow />
  </div>
);

// ─── Document Row ───────────────────────────────────────────────────────────────

const DocumentRow: React.FC<{
  doc: DocumentData;
  onClick?: (docId: string) => void;
  index: number;
}> = ({ doc, onClick, index }) => {
  const mimeType = doc.currentVersionId?.file?.mimeType;
  const IconComponent = getFileIcon(mimeType);
  const sizeBytes = doc.currentVersionId?.file?.sizeBytes;

  return (
    <motion.div
      initial={{ opacity: 0, x: -8 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: index * 0.03, duration: 0.2 }}
      onClick={() => onClick?.(doc._id)}
      className="flex items-center gap-3 px-4 py-2.5 hover:bg-slate-50 cursor-pointer transition-colors border-t border-slate-100"
    >
      <div className="pl-6 flex items-center gap-3 flex-1 min-w-0">
        <IconComponent className="h-4 w-4 text-slate-400 flex-shrink-0" />
        <span className="text-sm font-medium truncate" style={{ color: "#1F2937" }}>
          {doc.title}
        </span>
      </div>
      <div className="flex items-center gap-1 text-xs text-slate-500 flex-shrink-0">
        <Calendar className="h-3 w-3" />
        {formatDate(doc.createdAt)}
      </div>
      <span className="text-xs text-slate-400 w-16 text-right flex-shrink-0">
        {sizeBytes ? formatBytes(sizeBytes) : "--"}
      </span>
    </motion.div>
  );
};

// ─── Matter Section ─────────────────────────────────────────────────────────────

const MatterSection: React.FC<{
  group: MatterGroup;
  onDocumentClick?: (docId: string) => void;
  defaultOpen?: boolean;
}> = ({ group, onDocumentClick, defaultOpen = true }) => {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden mb-3">
      {/* Matter header */}
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center gap-3 px-4 py-3 hover:bg-slate-50 transition-colors text-left"
      >
        <motion.div
          animate={{ rotate: open ? 90 : 0 }}
          transition={{ duration: 0.15 }}
        >
          <ChevronRight className="h-4 w-4 text-slate-400" />
        </motion.div>
        {open ? (
          <FolderOpen className="h-4 w-4 flex-shrink-0" style={{ color: "var(--color-primary)" }} />
        ) : (
          <Folder className="h-4 w-4 flex-shrink-0" style={{ color: "var(--color-primary)" }} />
        )}
        <span className="text-sm font-semibold flex-1 truncate" style={{ color: "#1F2937" }}>
          {group.matter.title}
        </span>
        {group.matter.matterNumber && (
          <span className="px-2 py-0.5 bg-slate-100 text-slate-600 rounded-full text-xs font-medium flex-shrink-0">
            {group.matter.matterNumber}
          </span>
        )}
        <span className="px-2 py-0.5 bg-brand-light text-brand rounded-full text-xs font-medium flex-shrink-0">
          {group.documents.length} doc{group.documents.length !== 1 ? "s" : ""}
        </span>
      </button>

      {/* Documents list */}
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2, ease: "easeInOut" }}
            style={{ overflow: "hidden" }}
          >
            {group.documents.length === 0 ? (
              <div className="px-4 py-4 pl-12 text-sm text-slate-400 border-t border-slate-100">
                No documents
              </div>
            ) : (
              group.documents.map((doc, index) => (
                <DocumentRow
                  key={doc._id}
                  doc={doc}
                  onClick={onDocumentClick}
                  index={index}
                />
              ))
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// ─── General Documents Section ──────────────────────────────────────────────────

const GeneralSection: React.FC<{
  documents: DocumentData[];
  onDocumentClick?: (docId: string) => void;
}> = ({ documents, onDocumentClick }) => {
  const [open, setOpen] = useState(true);

  if (documents.length === 0) return null;

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden mb-3">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center gap-3 px-4 py-3 hover:bg-slate-50 transition-colors text-left"
      >
        <motion.div
          animate={{ rotate: open ? 90 : 0 }}
          transition={{ duration: 0.15 }}
        >
          <ChevronRight className="h-4 w-4 text-slate-400" />
        </motion.div>
        {open ? (
          <FolderOpen className="h-4 w-4 text-slate-500 flex-shrink-0" />
        ) : (
          <Folder className="h-4 w-4 text-slate-500 flex-shrink-0" />
        )}
        <span className="text-sm font-semibold flex-1 text-slate-600">
          General Documents
        </span>
        <span className="px-2 py-0.5 bg-slate-100 text-slate-600 rounded-full text-xs font-medium flex-shrink-0">
          {documents.length} doc{documents.length !== 1 ? "s" : ""}
        </span>
      </button>

      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2, ease: "easeInOut" }}
            style={{ overflow: "hidden" }}
          >
            {documents.map((doc, index) => (
              <DocumentRow
                key={doc._id}
                doc={doc}
                onClick={onDocumentClick}
                index={index}
              />
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// ─── Main Component ─────────────────────────────────────────────────────────────

const DocumentTree: React.FC<DocumentTreeProps> = ({
  matters,
  unassigned,
  onDocumentClick,
  loading = false,
}) => {
  // Loading state
  if (loading) {
    return (
      <div>
        <SkeletonSection />
        <SkeletonSection />
        <SkeletonSection />
      </div>
    );
  }

  // Overall empty state
  if (matters.length === 0 && unassigned.length === 0) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-xl shadow-sm border border-slate-200 p-16 text-center"
      >
        <Folder className="h-16 w-16 mx-auto text-slate-300 mb-4" />
        <h3 className="text-lg font-semibold text-slate-700 mb-2">
          No documents found
        </h3>
        <p className="text-sm text-slate-500">
          Documents will appear here once they are uploaded and associated.
        </p>
      </motion.div>
    );
  }

  return (
    <div>
      {matters.map((group) => (
        <MatterSection
          key={group.matter._id}
          group={group}
          onDocumentClick={onDocumentClick}
        />
      ))}
      <GeneralSection documents={unassigned} onDocumentClick={onDocumentClick} />
    </div>
  );
};

export default DocumentTree;
